import logger from '@ayocore/platform/common/helpers/logger';
import type { AwaitableRpcClient } from '@ayocore/platform/server/rpc/types';
import type { CommentMention } from '@ayocore/comments/common/types/comment-mention';
import type { CommentContactMention } from '@ayocore/comments/common/types/comment-contact-mention';
import type { CommentRepository } from '@ayocore/comments/server/repositories/CommentRepository';
import type { TaskRepository } from '@ayocore/tasks/server/repositories/TaskRepository';
import type { TaskStatusRepository } from '@ayocore/tasks/server/repositories/TaskStatusRepository';
import type { ActivityCommentScope } from '@ayocore/activity/common/types/activity-comment-scope';
import type { ActivityChatItem } from '@ayocore/activity/common/types/activity-message';
import type { ActivityEventRepository } from '@ayocore/activity/server/repositories/ActivityEventRepository';
import type { ActivityContextPreview } from '../types/activity-context';
import { normalizeActivityScope } from '../utils/normalize-activity-scope';
import { ControllerStore } from './ControllerStore';
import { INITIAL_CHAT_STATE, type ActivityChatState } from './types';

const COMMENTS_PAGE_SIZE = 20;
const CHAT_WINDOW_STEP = 20;
const CHAT_WINDOW_MAX = 400;
const NOT_FOUND_MESSAGE = 'Запись не найдена';

type ChatRepositories = {
  eventRepository: AwaitableRpcClient<ActivityEventRepository>;
  commentRepository: AwaitableRpcClient<CommentRepository>;
  taskRepository: AwaitableRpcClient<TaskRepository>;
  taskStatusRepository: AwaitableRpcClient<TaskStatusRepository>;
};

/**
 * ActivityChatController — состояние и действия правой панели чата.
 */
export class ActivityChatController extends ControllerStore<ActivityChatState> {
  private eventRepository: AwaitableRpcClient<ActivityEventRepository>;

  private commentRepository: AwaitableRpcClient<CommentRepository>;

  private taskRepository: AwaitableRpcClient<TaskRepository>;

  private taskStatusRepository: AwaitableRpcClient<TaskStatusRepository>;

  private chatReloadTimer: ReturnType<typeof setTimeout> | null = null;

  private chatRequestSeq = 0;

  private contextRequestSeq = 0;

  private chatWindowByThread = new Map<number, number>();

  private activeThreadId: number | null = null;

  constructor(repositories: ChatRepositories) {
    super(INITIAL_CHAT_STATE);
    this.eventRepository = repositories.eventRepository;
    this.commentRepository = repositories.commentRepository;
    this.taskRepository = repositories.taskRepository;
    this.taskStatusRepository = repositories.taskStatusRepository;
  }

  /**
   * Обновляет rpc-зависимости chat-контроллера.
   */
  updateRepositories(repositories: Partial<ChatRepositories>): void {
    if (repositories.eventRepository) this.eventRepository = repositories.eventRepository;
    if (repositories.commentRepository) this.commentRepository = repositories.commentRepository;
    if (repositories.taskRepository) this.taskRepository = repositories.taskRepository;
    if (repositories.taskStatusRepository)
      this.taskStatusRepository = repositories.taskStatusRepository;
  }

  /**
   * Инвалидирует pending chat/context запросы и очищает debounce таймер.
   */
  invalidatePending(): void {
    this.chatRequestSeq += 1;
    this.contextRequestSeq += 1;
    if (this.chatReloadTimer) {
      clearTimeout(this.chatReloadTimer);
      this.chatReloadTimer = null;
    }
  }

  /**
   * Фиксирует активный тред для stale-check и reload-логики.
   */
  setActiveThreadId(threadId: number | null): void {
    this.activeThreadId = threadId;
  }

  /**
   * Очищает transient chat-state при смене/сбросе выбранного треда.
   */
  clearThreadData(): void {
    if (this.chatReloadTimer) {
      clearTimeout(this.chatReloadTimer);
      this.chatReloadTimer = null;
    }
    this.setState({
      chatItems: [],
      commentsHasMoreOlder: false,
      loadingOlderComments: false,
      commentsLoading: false,
      commentsErrorMessage: null,
      eventsLoading: false,
      eventsErrorMessage: null,
      sendingComment: false,
      replyTarget: null,
      jumpToCommentUuid: null,
      scopePreset: null,
      activeContext: null,
      editComment: {
        showEditComment: false,
        editingCommentUuid: null,
        editingCommentText: '',
        isSavingComment: false,
      },
    });
  }

  /**
   * Планирует debounce-перезагрузку чата.
   */
  scheduleChatReload(threadId: number | null, debounceMs: number = 150): void {
    if (this.chatReloadTimer) {
      clearTimeout(this.chatReloadTimer);
    }
    this.chatReloadTimer = setTimeout(() => {
      if (!threadId || this.activeThreadId !== threadId) return;
      void this.loadChat(threadId, { showLoader: false });
    }, debounceMs);
  }

  /**
   * Загружает окно сообщений выбранного треда.
   */
  async loadChat(
    threadId: number | null,
    options?: { showLoader?: boolean; windowSize?: number }
  ): Promise<void> {
    const showLoader = options?.showLoader ?? true;
    this.activeThreadId = threadId;

    if (!threadId || threadId <= 0) {
      this.setState({
        chatItems: [],
        commentsHasMoreOlder: false,
        commentsLoading: false,
        commentsErrorMessage: null,
        eventsLoading: false,
        eventsErrorMessage: null,
        loadingOlderComments: false,
      });
      return;
    }

    const requestId = ++this.chatRequestSeq;
    try {
      logger.debug('[Activity:Chat] loadChat started', { threadId, showLoader });

      const currentWindow =
        typeof options?.windowSize === 'number' && options.windowSize > 0
          ? this.setChatWindowSize(threadId, options.windowSize)
          : this.getChatWindowSize(threadId);

      if (showLoader) {
        this.setState({
          commentsLoading: true,
          commentsErrorMessage: null,
          eventsLoading: true,
          eventsErrorMessage: null,
        });
      }

      const fetchLimit = Math.max(COMMENTS_PAGE_SIZE, Math.min(CHAT_WINDOW_MAX, currentWindow));
      const items = await this.eventRepository.getThreadChat(threadId, {
        totalLimit: fetchLimit,
      });
      if (this.isChatRequestStale(requestId, threadId)) {
        logger.debug('[Activity:Chat] request stale, skipping', {
          requestId,
          currentSeq: this.chatRequestSeq,
        });
        return;
      }

      const normalizedItems = Array.isArray(items) ? items : [];
      this.setState({
        chatItems: normalizedItems,
        commentsHasMoreOlder: normalizedItems.length >= fetchLimit,
        loadingOlderComments: false,
        commentsErrorMessage: null,
        eventsErrorMessage: null,
      });
      logger.debug('[Activity:Chat] loadChat completed', {
        threadId,
        itemsCount: normalizedItems.length,
      });
    } catch (error) {
      if (this.isChatRequestStale(requestId, threadId)) return;
      logger.error('[Activity:Chat] loadChat failed', { threadId, error });
      const errorMessage =
        error instanceof Error ? error.message : 'Не удалось загрузить сообщения треда.';
      this.setState({
        chatItems: [],
        commentsHasMoreOlder: false,
        loadingOlderComments: false,
        commentsErrorMessage: errorMessage,
        eventsErrorMessage: errorMessage,
      });
    } finally {
      if (!this.isChatRequestStale(requestId, threadId)) {
        this.setState({
          commentsLoading: false,
          eventsLoading: false,
        });
      }
    }
  }

  /**
   * Расширяет окно older messages для текущего треда.
   */
  async loadOlderChat(threadId: number | null): Promise<void> {
    if (!threadId || threadId <= 0) return;
    const snapshot = this.getSnapshot();
    if (snapshot.loadingOlderComments) return;
    if (!snapshot.commentsHasMoreOlder && snapshot.chatItems.length > 0) return;

    const requestId = ++this.chatRequestSeq;
    try {
      this.setState({
        loadingOlderComments: true,
        commentsErrorMessage: null,
        eventsErrorMessage: null,
      });

      const nextWindow = this.increaseChatWindowSize(threadId);
      const items = await this.eventRepository.getThreadChat(threadId, {
        totalLimit: nextWindow,
      });
      if (this.isChatRequestStale(requestId, threadId)) return;

      const normalizedItems = Array.isArray(items) ? items : [];
      this.setState({
        chatItems: normalizedItems,
        commentsHasMoreOlder: normalizedItems.length >= nextWindow,
        loadingOlderComments: false,
        commentsErrorMessage: null,
        eventsErrorMessage: null,
      });
    } catch (error) {
      if (this.isChatRequestStale(requestId, threadId)) return;
      logger.error('[Activity:Chat] loadOlderChat failed', { threadId, error });
      const errorMessage =
        error instanceof Error ? error.message : 'Не удалось загрузить старые сообщения.';
      this.setState({
        loadingOlderComments: false,
        commentsErrorMessage: errorMessage,
        eventsErrorMessage: errorMessage,
      });
    }
  }

  /**
   * Фокусирует чат вокруг конкретного комментария по anchor-event.
   */
  async focusComment(
    commentUuid: string,
    options?: {
      threadId?: number | null;
      anchorEventId?: number | null;
    }
  ): Promise<boolean> {
    const normalizedCommentUuid = commentUuid?.trim();
    const threadId = typeof options?.threadId === 'number' ? options.threadId : null;
    const anchorEventId =
      typeof options?.anchorEventId === 'number' && options.anchorEventId > 0
        ? options.anchorEventId
        : null;

    if (!normalizedCommentUuid || !threadId || !anchorEventId) {
      this.setState({
        commentsErrorMessage: NOT_FOUND_MESSAGE,
      });
      return false;
    }

    const hasCommentInCurrentWindow = this.getSnapshot().chatItems.some(
      item => item.kind === 'comment' && item.comment_uuid === normalizedCommentUuid
    );
    if (hasCommentInCurrentWindow) {
      if (this.getSnapshot().commentsErrorMessage === NOT_FOUND_MESSAGE) {
        this.setState({ commentsErrorMessage: null });
      }
      return true;
    }

    const requestId = ++this.chatRequestSeq;
    try {
      this.setState({
        commentsLoading: true,
        eventsLoading: true,
        commentsErrorMessage: null,
        eventsErrorMessage: null,
      });

      const aroundItems = await this.eventRepository.getThreadChatAroundEvent(
        threadId,
        anchorEventId,
        CHAT_WINDOW_STEP,
        CHAT_WINDOW_STEP
      );
      if (this.isChatRequestStale(requestId, threadId)) return false;

      const normalizedItems = Array.isArray(aroundItems) ? aroundItems : [];
      this.setChatWindowSize(threadId, Math.max(COMMENTS_PAGE_SIZE, normalizedItems.length));
      this.setState({
        chatItems: normalizedItems,
        commentsHasMoreOlder: normalizedItems.length >= CHAT_WINDOW_STEP * 2,
        commentsErrorMessage: null,
        eventsErrorMessage: null,
      });
    } catch (error) {
      if (this.isChatRequestStale(requestId, threadId)) return false;
      logger.error('[Activity:Chat] focusComment failed', {
        commentUuid: normalizedCommentUuid,
        threadId,
        anchorEventId,
        error,
      });
    } finally {
      if (!this.isChatRequestStale(requestId, threadId)) {
        this.setState({
          commentsLoading: false,
          eventsLoading: false,
        });
      }
    }

    if (this.isChatRequestStale(requestId, threadId)) return false;

    const found = this.getSnapshot().chatItems.some(
      item => item.kind === 'comment' && item.comment_uuid === normalizedCommentUuid
    );
    if (!found) {
      this.setState({ commentsErrorMessage: NOT_FOUND_MESSAGE });
      return false;
    }

    if (this.getSnapshot().commentsErrorMessage === NOT_FOUND_MESSAGE) {
      this.setState({ commentsErrorMessage: null });
    }
    return true;
  }

  /**
   * Отправляет комментарий с учётом reply/scope/mentions.
   */
  async sendComment(params: {
    entityType: string;
    entityUuid: string;
    content: string;
    threadId: number | null;
    composerScope?: ActivityCommentScope | null;
    parentCommentUuid?: string | null;
    mentionedUserIds?: number[] | null;
    mentionedUserMentions?: CommentMention[] | null;
    mentionedContactUuids?: string[] | null;
    mentionedContactMentions?: CommentContactMention[] | null;
    fileIds?: number[] | null;
  }): Promise<boolean> {
    const normalizedContent = params.content.trim();
    if (!normalizedContent || !params.entityType || !params.entityUuid) {
      return false;
    }

    try {
      logger.debug('[Activity:Chat] sendComment', {
        entityType: params.entityType,
        entityUuid: params.entityUuid,
        hasReply: Boolean(this.getSnapshot().replyTarget),
      });
      this.setState({
        sendingComment: true,
      });

      const normalizedMentionedUserIds = Array.isArray(params.mentionedUserIds)
        ? Array.from(
            new Set(
              params.mentionedUserIds.filter(userId => typeof userId === 'number' && userId > 0)
            )
          )
        : [];
      const normalizedMentionedUserMentions = Array.isArray(params.mentionedUserMentions)
        ? params.mentionedUserMentions
            .filter(
              mention =>
                mention &&
                typeof mention.user_id === 'number' &&
                mention.user_id > 0 &&
                typeof mention.label === 'string' &&
                mention.label.trim().length > 0 &&
                typeof mention.start === 'number' &&
                typeof mention.end === 'number' &&
                mention.start >= 0 &&
                mention.end > mention.start
            )
            .map(mention => ({
              user_id: Math.trunc(mention.user_id),
              label: mention.label.trim(),
              start: Math.trunc(mention.start),
              end: Math.trunc(mention.end),
            }))
        : [];
      const normalizedMentionedContactUuids = Array.isArray(params.mentionedContactUuids)
        ? Array.from(
            new Set(
              params.mentionedContactUuids.filter(
                uuid => typeof uuid === 'string' && uuid.trim().length > 0
              )
            )
          )
        : [];
      const normalizedFileIds = Array.isArray(params.fileIds)
        ? Array.from(
            new Set(
              params.fileIds.filter(id => typeof id === 'number' && Number.isFinite(id) && id > 0)
            )
          ).map(id => Math.trunc(id))
        : [];
      const normalizedMentionedContactMentions = Array.isArray(params.mentionedContactMentions)
        ? params.mentionedContactMentions
            .filter(
              mention =>
                mention &&
                typeof mention.uuid === 'string' &&
                mention.uuid.trim().length > 0 &&
                typeof mention.label === 'string' &&
                mention.label.trim().length > 0 &&
                typeof mention.start === 'number' &&
                typeof mention.end === 'number' &&
                mention.start >= 0 &&
                mention.end > mention.start
            )
            .map(mention => ({
              uuid: mention.uuid.trim(),
              label: mention.label.trim(),
              start: Math.trunc(mention.start),
              end: Math.trunc(mention.end),
            }))
        : [];

      const snapshot = this.getSnapshot();
      const effectiveScope =
        snapshot.activeContext?.scope ?? snapshot.scopePreset ?? params.composerScope ?? null;
      const normalizedScope =
        effectiveScope &&
        typeof effectiveScope.entity_type === 'string' &&
        typeof effectiveScope.entity_uuid === 'string' &&
        effectiveScope.entity_type.trim() &&
        effectiveScope.entity_uuid.trim()
          ? {
              entity_type: effectiveScope.entity_type.trim(),
              entity_uuid: effectiveScope.entity_uuid.trim(),
              ...(typeof effectiveScope.label === 'string' && effectiveScope.label.trim()
                ? { label: effectiveScope.label.trim() }
                : {}),
            }
          : null;

      await this.commentRepository.createExtended({
        entity_type: params.entityType,
        entity_uuid: params.entityUuid,
        content: normalizedContent,
        ...(snapshot.replyTarget?.commentUuid
          ? { parent_comment_uuid: snapshot.replyTarget.commentUuid }
          : {}),
        ...(normalizedMentionedUserIds.length > 0
          ? { mentioned_user_ids: normalizedMentionedUserIds }
          : {}),
        ...(normalizedMentionedUserMentions.length > 0
          ? { mentioned_user_mentions: normalizedMentionedUserMentions }
          : {}),
        ...(normalizedMentionedContactUuids.length > 0
          ? { mentioned_contact_uuids: normalizedMentionedContactUuids }
          : {}),
        ...(normalizedMentionedContactMentions.length > 0
          ? { mentioned_contact_mentions: normalizedMentionedContactMentions }
          : {}),
        ...(normalizedScope ? { scope: normalizedScope } : {}),
        ...(normalizedFileIds.length > 0 ? { file_ids: normalizedFileIds } : {}),
      });

      if (params.threadId && params.threadId > 0) {
        await this.loadChat(params.threadId, { showLoader: false });
      }

      this.setState({
        replyTarget: null,
      });
      return true;
    } catch (error) {
      logger.error('[Activity:Chat] sendComment failed', { error });
      this.setState({
        commentsErrorMessage:
          error instanceof Error ? error.message : 'Не удалось отправить комментарий.',
      });
      return false;
    } finally {
      this.setState({
        sendingComment: false,
      });
    }
  }

  /**
   * Переводит composer в reply-режим по выбранному сообщению.
   */
  startReply(item: ActivityChatItem): void {
    const commentUuid = item.comment_uuid;
    if (!commentUuid) return;
    this.contextRequestSeq += 1;
    this.setState({
      activeContext: null,
      replyTarget: {
        commentUuid,
        preview: this.buildReplyPreview(item.content),
      },
      scopePreset: normalizeActivityScope(item.scope),
    });
  }

  /**
   * Сбрасывает reply-режим.
   */
  cancelReply(): void {
    this.setState({
      replyTarget: null,
    });
  }

  /**
   * Открывает форму редактирования комментария.
   */
  openEditComment(commentUuid: string, content: string): void {
    this.setState({
      editComment: {
        showEditComment: true,
        editingCommentUuid: commentUuid,
        editingCommentText: content,
        isSavingComment: false,
      },
    });
  }

  /**
   * Закрывает форму редактирования комментария.
   */
  closeEditComment(): void {
    this.setState({
      editComment: {
        showEditComment: false,
        editingCommentUuid: null,
        editingCommentText: '',
        isSavingComment: false,
      },
    });
  }

  /**
   * Обновляет draft текста редактируемого комментария.
   */
  setEditingCommentText(nextValue: string): void {
    this.setState({
      editComment: {
        ...this.getSnapshot().editComment,
        editingCommentText: nextValue,
      },
    });
  }

  /**
   * Сохраняет изменения редактируемого комментария.
   */
  async saveEditedComment(): Promise<void> {
    const { editingCommentUuid, editingCommentText } = this.getSnapshot().editComment;
    const content = editingCommentText.trim();
    if (!editingCommentUuid || !content) return;

    this.setState({
      editComment: {
        ...this.getSnapshot().editComment,
        isSavingComment: true,
      },
    });

    try {
      await this.commentRepository.update(editingCommentUuid, { content });
      this.closeEditComment();
    } catch (error) {
      logger.error('[Activity:Chat] saveEditedComment failed', {
        editingCommentUuid,
        error,
      });
    } finally {
      this.setState({
        editComment: {
          ...this.getSnapshot().editComment,
          isSavingComment: false,
        },
      });
    }
  }

  /**
   * Удаляет комментарий и синхронизирует связанный transient state.
   */
  async deleteComment(commentUuid: string): Promise<void> {
    const normalizedCommentUuid = commentUuid?.trim();
    if (!normalizedCommentUuid) return;

    try {
      await this.commentRepository.deleteById(normalizedCommentUuid);
      if (this.getSnapshot().replyTarget?.commentUuid === normalizedCommentUuid) {
        this.setState({ replyTarget: null });
      }
      if (this.getSnapshot().editComment.editingCommentUuid === normalizedCommentUuid) {
        this.closeEditComment();
      }
    } catch (error) {
      logger.error('[Activity:Chat] deleteComment failed', {
        commentUuid: normalizedCommentUuid,
        error,
      });
    }
  }

  /**
   * Помечает scopePreset как обработанный composer-слоем.
   */
  consumeScopePreset(): void {
    if (this.getSnapshot().scopePreset === null) return;
    this.setState({
      scopePreset: null,
    });
  }

  /**
   * Закрывает активный task/context блок и очищает scopePreset.
   */
  closeActiveContext(): void {
    this.contextRequestSeq += 1;
    this.setState({
      activeContext: null,
      scopePreset: null,
    });
  }

  /**
   * Запрашивает jump к сообщению по comment_uuid.
   */
  requestJumpToComment(commentUuid: string): void {
    const normalizedCommentUuid = commentUuid?.trim();
    if (!normalizedCommentUuid) return;
    this.setState({
      jumpToCommentUuid: normalizedCommentUuid,
    });
  }

  /**
   * Подтверждает обработку jump-to-comment в viewport-слое.
   */
  handleJumpToCommentHandled(): void {
    this.setState({
      jumpToCommentUuid: null,
    });
  }

  /**
   * Открывает универсальный custom-context surface по slug.
   * Используется для единого host-flow (`ActivityContextView`) для slash/pin/comment actions.
   */
  openCustomContext(input: {
    slug: string;
    title: string;
    subtitle?: string;
    scope: ActivityCommentScope;
    preview?: ActivityContextPreview | null;
  }): void {
    const slug = input.slug?.trim();
    const scopeEntityType =
      typeof input.scope.entity_type === 'string' ? input.scope.entity_type.trim() : '';
    const scopeEntityUuid =
      typeof input.scope.entity_uuid === 'string' ? input.scope.entity_uuid.trim() : '';
    const scopeLabel = typeof input.scope.label === 'string' ? input.scope.label.trim() : '';
    if (!slug || !scopeEntityType || !scopeEntityUuid) return;

    const contextTitle = input.title?.trim();
    if (!contextTitle) return;

    this.contextRequestSeq += 1;
    const contextId = `${slug}:${this.contextRequestSeq}`;
    const subtitle = typeof input.subtitle === 'string' ? input.subtitle.trim() : '';
    this.setState({
      replyTarget: null,
      activeContext: {
        kind: 'custom',
        id: contextId,
        title: contextTitle,
        ...(subtitle ? { subtitle } : {}),
        scope: {
          entity_type: scopeEntityType,
          entity_uuid: scopeEntityUuid,
          ...(scopeLabel ? { label: scopeLabel } : {}),
        },
        customKind: slug,
        preview: input.preview ?? null,
      },
      scopePreset: {
        entity_type: scopeEntityType,
        entity_uuid: scopeEntityUuid,
        ...(scopeLabel ? { label: scopeLabel } : {}),
      },
    });
  }

  /**
   * Завершает задачу из пина (перевод в FINISHED статус).
   */
  async completeTask(taskId: number, taskTypeId: number | null): Promise<void> {
    if (!Number.isFinite(taskId) || taskId <= 0) return;
    try {
      let resolvedTaskTypeId =
        Number.isFinite(taskTypeId) && (taskTypeId ?? 0) > 0 ? taskTypeId : null;
      if (!resolvedTaskTypeId) {
        const task = await this.taskRepository.findById(taskId);
        resolvedTaskTypeId = task?.task_type_id ?? null;
      }
      if (!resolvedTaskTypeId) return;

      const statuses = await this.taskStatusRepository.findByTaskType(resolvedTaskTypeId);
      const finishedStatus = statuses.find(status => status.slug === 'FINISHED');
      if (!finishedStatus) return;
      await this.taskRepository.update(taskId, { task_status_id: finishedStatus.id });
    } catch (error) {
      logger.error('[Activity:Chat] completeTask failed', { taskId, error });
    }
  }

  /**
   * Удаляет задачу из пина.
   */
  async deleteTask(taskId: number): Promise<void> {
    if (!Number.isFinite(taskId) || taskId <= 0) return;
    try {
      await this.taskRepository.deleteById(taskId);
    } catch (error) {
      logger.error('[Activity:Chat] deleteTask failed', { taskId, error });
    }
  }

  /**
   * Полный reset на unmount.
   */
  reset(): void {
    this.chatWindowByThread.clear();
    this.clearThreadData();
    this.activeThreadId = null;
  }

  /**
   * Освобождает ресурсы chat-контроллера.
   */
  dispose(): void {
    if (this.chatReloadTimer) {
      clearTimeout(this.chatReloadTimer);
      this.chatReloadTimer = null;
    }
    this.chatWindowByThread.clear();
    super.dispose();
  }

  private isChatRequestStale(requestId: number, threadId: number): boolean {
    return requestId !== this.chatRequestSeq || this.activeThreadId !== threadId;
  }

  private getChatWindowSize(threadId: number): number {
    const current = this.chatWindowByThread.get(threadId);
    if (typeof current === 'number' && current > 0) return current;
    return COMMENTS_PAGE_SIZE;
  }

  private setChatWindowSize(threadId: number, value: number): number {
    const normalized = Math.max(COMMENTS_PAGE_SIZE, Math.min(CHAT_WINDOW_MAX, value));
    this.chatWindowByThread.set(threadId, normalized);
    return normalized;
  }

  private increaseChatWindowSize(threadId: number): number {
    return this.setChatWindowSize(threadId, this.getChatWindowSize(threadId) + CHAT_WINDOW_STEP);
  }

  private buildReplyPreview(content: string): string {
    const normalized = (content ?? '').replace(/\s+/g, ' ').trim();
    if (!normalized) return 'Ответ на комментарий';
    return normalized.length > 140 ? `${normalized.slice(0, 137)}...` : normalized;
  }
}
